import type { CrudFormProps } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'
import type { SupervisorFormState } from './SupervisorsFormState'

import React, { useCallback, useEffect, useMemo } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useFetchSingleSupervisor } from '@@partner:graphql/query/employees/singleSupervisor.gql'
import { useMutateSaveSupervisor } from '@@partner:graphql/mutation/employees/saveSupervisor.gql'
import { useFetchShops } from '../../../graphql/query/shop/shops.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  notifyOk,
  notifyError,
  makeGraphQLErrorNotification
} from '@@partner:helpers/notification'
import {
  makeSupervisorFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './SupervisorsFormState'

import {
  CheckPicker,
  Form,
  SelectPicker,
  Message,
  Grid,
  Row,
  Col
} from 'rsuite'
import CustomerPhone from '@components/order/form/CustomerPhone'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'

import { SupervisorFormValidationModel } from '../validation/SupervisorFormValidationModel'

import { EMPLOYEE_STATUS_LABELS } from '@@partner:config/employee'
import FormCheckbox from '@components/common/form/FormCheckbox'

export interface SupervisorFormProps extends CrudFormProps {
  id?: GQL_ID
}

export default function SupervisorForm ({ id, onSaved }: SupervisorFormProps) {
  const {
    data,
    loading: loadingSupervisor,
    error
  } = useFetchSingleSupervisor({ id })
  const [ saveSupervisor, { loading: loadingSaveSupervisor } ] =
    useMutateSaveSupervisor()

  const { data: shopsData, loading: loadingShopsData } = useFetchShops()

  const shops = useMemo(
    () =>
      shopsData?.shops?.map(
        (item) => ({ label: item.name, value: Number(item.id) } ?? [])
      ),
    [ shopsData?.shops ]
  )

  const {
    formState,
    setFormState,
    setFormRef,
    handleFormChange,
    handleFormError
  } = useValidatableForm<SupervisorFormState>(makeSupervisorFormState)

  useEffect(() => {
    if (!loadingSupervisor && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.supervisor))
      )
    }
  }, [ data, loadingSupervisor, setFormState ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveSupervisor({
          variables: {
            supervisorInfo: mapStateToApiVariable(formState),
            upsert: !id
          }
        })
          .then(({ data }) => {
            notifyOk('Данные супервайзера сохранёны')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.supervisor)
            )
            onSaved?.(data?.supervisor.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, id, onSaved, saveSupervisor, setFormState ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки супервайзера"
      />
    )
  }

  return (
    <div className="CouriersForm">
      <Form
        model={SupervisorFormValidationModel}
        formValue={formState}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCheck={handleFormError}
        ref={setFormRef}
        onError={handleFormError}
        layout="horizontal"
      >
        <h3 className="u-form-heading">Супервайзер</h3>

        <Grid>
          <Row>
            <Col md={12}>
              <Form.Group>
                <Form.ControlLabel>Фамилия</Form.ControlLabel>
                <Form.Control name="firstName" placeholder="Фамилия" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Имя</Form.ControlLabel>
                <Form.Control name="lastName" placeholder="Имя" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>E-mail</Form.ControlLabel>
                <Form.Control name="email" placeholder="E-mail" />
              </Form.Group>
              {!id ? (
                <Form.Group>
                  <Form.ControlLabel>Пароль</Form.ControlLabel>
                  <Form.Control
                    name="password"
                    placeholder="Пароль"
                    type="password"
                  />
                </Form.Group>
              ) : null}
              <Form.Group>
                <CustomerPhone name="phone" label="Телефон" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Статус</Form.ControlLabel>
                <Form.Control
                  name="status"
                  accepter={SelectPicker}
                  data={EMPLOYEE_STATUS_LABELS}
                  searchable={false}
                  cleanable={false}
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Магазин</Form.ControlLabel>
                <Form.Control
                  name="shopIds"
                  accepter={CheckPicker}
                  loading={loadingShopsData}
                  data={shops}
                  checked={formState.shopIds}
                  searchable={false}
                />
              </Form.Group>
            </Col>

            {!!id ? (
              <Col md={12}>
                <Message
                  className="p-4 max-w-lg"
                  type="error"
                  header="Опасные действия"
                  showIcon
                >
                  <Form.Group>
                    <Form.ControlLabel>Изменить пароль</Form.ControlLabel>
                    <FormCheckbox.Control
                      name="changePassword"
                      placeholder="Изменить пароль"
                    />
                    {formState.changePassword ? (
                      <>
                        <Form.Control
                          className="my-1"
                          type="password"
                          name="password"
                          placeholder="Пароль"
                        />
                        <Form.Control
                          className="my-1"
                          type="password"
                          name="repeatPassword"
                          placeholder="Повторите"
                        />
                      </>
                    ) : null}
                  </Form.Group>
                </Message>
              </Col>
            ) : null}
          </Row>
        </Grid>

        <SaveButton
          loading={loadingSupervisor || loadingSaveSupervisor}
          disabled={loadingSupervisor || loadingSaveSupervisor}
        >
          Сохранить
        </SaveButton>
      </Form>
    </div>
  )
}
